/**
 * NotificationService.js
 * 알림 서비스 (퇴근 시간 넛지, 에스컬레이션)
 * 브라우저 알림 API 사용 + FCM (Firebase Cloud Messaging) 지원
 */

import { initializeMessaging, getFCMToken, setupForegroundMessageHandler, getMessagingInstance, VAPID_KEY } from '../config/firebase';
import apiConfig from '../config/api';

class NotificationService {
  constructor() {
    this.permission = null;
    this.fcmMessaging = null;
    this.fcmToken = null;
    this.checkPermission();
    this.initializeFCM();
  }

  // FCM 초기화
  async initializeFCM() {
    try {
      this.fcmMessaging = await initializeMessaging();
      if (this.fcmMessaging) {
        // 포그라운드 메시지 핸들러 설정
        setupForegroundMessageHandler(this.fcmMessaging, (payload) => {
          this.handleForegroundMessage(payload);
        });
      } else {
        // Firebase 설정이 없거나 FCM을 지원하지 않는 경우
        // 에러 로그는 firebase.js에서 처리하므로 여기서는 별도 로그 없음
      }
    } catch (error) {
      // Firebase 설정이 없을 때 발생할 수 있는 오류는 무시
      if (process.env.NODE_ENV === 'development') {
        console.warn('[NotificationService] FCM 초기화 실패 (Firebase 설정 없음일 수 있음):', error.message);
      }
    }
  }

  // 포그라운드 메시지 처리
  handleForegroundMessage(payload) {
    const { notification, data } = payload;
    
    if (notification) {
      this.showNotification(notification.title, {
        body: notification.body,
        icon: notification.icon || '/favicon.ico',
        tag: data?.tag || 'fcm-notification',
        data: data,
        onClick: () => {
          if (data?.url) {
            window.location.href = data.url;
          }
        },
      });
      
      // 리액션 알림인 경우 알림 목록 새로고침 이벤트 발생
      if (data?.type === 'reaction') {
        window.dispatchEvent(new CustomEvent('notification-received', {
          detail: { type: 'reaction', data: data }
        }));
      }
    }
  }

  // 알림 권한 확인 및 요청
  async checkPermission() {
    if (!('Notification' in window)) {
      console.warn('[NotificationService] 이 브라우저는 알림을 지원하지 않습니다.');
      return false;
    }

    const currentPermission = Notification.permission;
    
    if (currentPermission === 'granted') {
      this.permission = 'granted';
      // 프로덕션 환경에서도 권한 상태는 로그 출력
      if (process.env.NODE_ENV === 'development') {
        console.log('[NotificationService] 알림 권한: granted');
      }
      return true;
    } else if (currentPermission === 'denied') {
      this.permission = 'denied';
      // 권한 거부는 중요한 상태이므로 항상 로그 출력
      console.warn('[NotificationService] 알림 권한이 거부되었습니다. 사용자가 브라우저 설정에서 변경해야 합니다.');
      return false;
    } else {
      // 기본값: 요청 필요
      const permission = await Notification.requestPermission();
      this.permission = permission;
      // 권한 요청 결과는 중요한 이벤트이므로 항상 로그 출력
      console.log('[NotificationService] 알림 권한 요청 결과:', permission);
      return permission === 'granted';
    }
  }

  // 권한 상태 확인 (로그인 시마다 호출 - 팝업 표시 안 함)
  checkNotificationPermission() {
    if (!('Notification' in window)) {
      return { 
        status: 'unsupported', 
        canRequest: false, 
        message: '이 브라우저는 알림을 지원하지 않습니다.' 
      };
    }

    const permission = Notification.permission;
    
    // granted: 토큰 발급 진행
    if (permission === 'granted') {
      return { 
        status: 'granted', 
        canRequest: false, 
        message: '알림 권한이 활성화되어 있습니다.' 
      };
    }
    
    // denied: 재요청 불가 (브라우저 설정 필요)
    if (permission === 'denied') {
      return { 
        status: 'denied', 
        canRequest: false, 
        message: '알림이 거부되었습니다. 브라우저 설정에서 변경해주세요.' 
      };
    }
    
    // default: 요청 가능
    return { 
      status: 'default', 
      canRequest: true, 
      message: '알림 권한을 허용하면 투입시간 리마인더를 받을 수 있습니다.' 
    };
  }

  // 알림 표시
  async showNotification(title, options = {}) {
    if (!(await this.checkPermission())) {
      console.warn('알림 권한이 없습니다.');
      return null;
    }

    const defaultOptions = {
      body: options.body || '',
      icon: options.icon || '/favicon.ico',
      badge: options.badge || '/favicon.ico',
      tag: options.tag || 'otl-notification',
      requireInteraction: options.requireInteraction || false,
      silent: options.silent || false,
    };

    try {
      const notification = new Notification(title, defaultOptions);
      
      // 클릭 시 앱으로 이동
      notification.onclick = () => {
        window.focus();
        if (options.onClick) {
          options.onClick();
        }
        notification.close();
      };

      // 자동 닫기 (5초 후)
      if (!options.requireInteraction) {
        setTimeout(() => {
          notification.close();
        }, options.duration || 5000);
      }

      return notification;
    } catch (error) {
      console.error('알림 표시 실패:', error);
      return null;
    }
  }

  // 퇴근 시간 넛지 (오후 5:30)
  async showEndOfDayNudge() {
    return await this.showNotification('OnTimeLog 입력 알림', {
      body: '오늘의 OnTimeLog를 2분 만에 완료하세요.',
      tag: 'end-of-day-nudge',
      requireInteraction: true,
      onClick: () => {
        window.location.href = '/job-time-input';
      },
    });
  }

  // 어제 로그 누락 알림 (D+1 오전 9:00)
  async showMissingLogAlert(missingDate) {
    return await this.showNotification('OnTimeLog 누락 알림', {
      body: `어제(${missingDate})의 로그가 비어있습니다. 잊기 전에 입력하세요.`,
      tag: 'missing-log-alert',
      requireInteraction: true,
      onClick: () => {
        window.location.href = '/job-time-input';
      },
    });
  }

  // 에스컬레이션 알림 (Level 2: 매니저 참조)
  async showEscalationAlert(employeeName, missingDays) {
    return await this.showNotification('OnTimeLog 입력 지연', {
      body: `${employeeName}님 입력 누락: ${missingDays}일`,
      tag: 'escalation-alert',
      requireInteraction: true,
    });
  }

  // 스케줄러 시작 (퇴근 시간 넛지)
  startEndOfDayScheduler(callbackTime = '17:30') {
    const [hours, minutes] = callbackTime.split(':').map(Number);
    
    const checkAndNotify = () => {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      
      // 설정된 시간에 정확히 알림
      if (currentHour === hours && currentMinute === minutes) {
        this.showEndOfDayNudge();
      }
    };

    // 매 분마다 확인
    setInterval(checkAndNotify, 60000);
    
    // 즉시 확인 (현재 시간이 설정 시간과 같으면)
    checkAndNotify();
  }

  // 스케줄러 시작 (출근 시간 캐치업)
  startMorningCatchupScheduler(callbackTime = '09:00') {
    const [hours, minutes] = callbackTime.split(':').map(Number);
    
    const checkAndNotify = async () => {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      
      // 설정된 시간에 정확히 알림
      if (currentHour === hours && currentMinute === minutes) {
        // 어제 날짜 확인
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = yesterday.toISOString().split('T')[0];
        
        // API 호출하여 어제 로그 확인
        try {
          const response = await fetch(`/api/v1/timelogs/my?date=${yesterdayStr}`);
          const data = await response.json();
          
          if (!data || data.length === 0) {
            await this.showMissingLogAlert(yesterdayStr);
          }
        } catch (error) {
          console.error('어제 로그 확인 실패:', error);
        }
      }
    };

    // 매 분마다 확인
    setInterval(checkAndNotify, 60000);
    
    // 즉시 확인 (현재 시간이 설정 시간과 같으면)
    checkAndNotify();
  }

  // 당일 투입시간 알림 모달 표시 콜백
  setDailyTimeModalCallback(callback) {
    this.dailyTimeModalCallback = callback;
  }

  // 당일 투입시간 알림 모달 표시
  showDailyTimeModal() {
    if (this.dailyTimeModalCallback) {
      this.dailyTimeModalCallback();
    }
  }

  // Service Worker에 알림 요청 전송
  async requestServiceWorkerNotification(summary, isAuthenticated = true) {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      try {
        navigator.serviceWorker.controller.postMessage({
          type: 'SHOW_DAILY_TIME_NOTIFICATION',
          summary: summary,
          isAuthenticated: isAuthenticated,
        });
        console.log('[NotificationService] Service Worker에 알림 요청 전송 (인증 상태:', isAuthenticated, ')');
      } catch (error) {
        console.error('[NotificationService] Service Worker 메시지 전송 실패:', error);
      }
    }
  }

  // 스케줄러 시작 (당일 투입시간 알림 - 설정된 시간 사용)
  startDailyTimeNotificationScheduler() {
    // 마지막 알림 시간 추적 (중복 알림 방지)
    const lastNotificationTimes = {};

    const checkAndNotify = async () => {
      // 설정된 알림 시간 가져오기 (없으면 알림 비활성화)
      let notificationTimes = [];
      let excludeSaturday = false;  // 토요일 제외 여부
      let excludeSunday = false;  // 일요일 제외 여부
      
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const response = await fetch('/api/v1/admin/notification-times', {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
          
          if (response.ok) {
            const config = await response.json();
            
            // 주말 제외 설정 가져오기
            excludeSaturday = config.exclude_saturday || false;
            excludeSunday = config.exclude_sunday || false;
            
            if (config.times && config.times.length > 0) {
              notificationTimes = config.times.map(time => ({
                hour: time.hour,
                minute: time.minute,
                label: `${time.hour >= 12 ? '오후' : '오전'} ${time.hour > 12 ? time.hour - 12 : time.hour === 0 ? 12 : time.hour}시 ${time.minute > 0 ? time.minute + '분' : ''}`
              }));
              
              // 개발 환경에서만 로그 출력
              if (process.env.NODE_ENV === 'development') {
                console.log('[NotificationService] 알림 시간 설정 조회 성공:', notificationTimes, {
                  excludeSaturday,
                  excludeSunday
                });
              }
            }
          }
        }
      } catch (error) {
        console.warn('[NotificationService] 알림 시간 설정 조회 실패:', error);
      }

      // 설정된 알림 시간이 없으면 알림 표시하지 않음
      if (notificationTimes.length === 0) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[NotificationService] 설정된 알림 시간이 없어 알림을 보내지 않습니다.');
        }
        return;
      }

      const now = new Date();
      const dayOfWeek = now.getDay(); // 0=일요일, 6=토요일
      
      // 토요일 제외 설정이 켜져 있고 오늘이 토요일이면 알림 발송하지 않음
      if (excludeSaturday && dayOfWeek === 6) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[NotificationService] 토요일 제외 설정이 활성화되어 있어 알림을 보내지 않습니다.');
        }
        return;
      }
      
      // 일요일 제외 설정이 켜져 있고 오늘이 일요일이면 알림 발송하지 않음
      if (excludeSunday && dayOfWeek === 0) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[NotificationService] 일요일 제외 설정이 활성화되어 있어 알림을 보내지 않습니다.');
        }
        return;
      }
      
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();

      // 개발 환경에서만 로그 출력
      if (process.env.NODE_ENV === 'development') {
        console.log(`[NotificationService] 알림 시간 확인: ${currentHour}:${currentMinute}`, {
          설정된시간: notificationTimes.map(t => `${t.hour}:${t.minute}`)
        });
      }

      for (const { hour, minute, label } of notificationTimes) {
        const timeKey = `${hour}:${minute}`;
        const lastNotification = lastNotificationTimes[timeKey];

        // 설정된 시간에 정확히 알림 (중복 방지)
        // 현재 시간이 설정된 시간과 일치하는지 확인
        const isTimeMatch = currentHour === hour && currentMinute === minute;
        // 같은 시간대에 5분 이내 중복 방지 (확인 주기가 짧을 때 중복 알림 방지)
        const isNotDuplicate = !lastNotification || (now.getTime() - lastNotification > 300000); // 5분 이내 중복 방지
        
        // 개발 환경에서만 로그 출력
        if (process.env.NODE_ENV === 'development') {
          console.log(`[NotificationService] 시간 체크: ${hour}:${minute}`, {
            현재시간: `${currentHour}:${currentMinute}`,
            일치: isTimeMatch,
            중복아님: isNotDuplicate,
            마지막알림: lastNotification ? new Date(lastNotification).toLocaleTimeString() : '없음'
          });
        }
        
        if (isTimeMatch && isNotDuplicate) {
          // 개발 환경에서만 로그 출력
          if (process.env.NODE_ENV === 'development') {
            console.log(`[NotificationService] ${label} 알림 트리거`);
          }
          
          // 인증 토큰 확인
          const token = localStorage.getItem('token');
          const isAuthenticated = !!token;

          // 당일 투입시간 요약 조회 (인증된 경우만)
          if (isAuthenticated) {
            try {
              const today = new Date().toISOString().split('T')[0];
              const response = await fetch(`/api/v1/timelogs/daily-summary?target_date=${today}`, {
                credentials: 'include',
                headers: {
                  'Authorization': `Bearer ${token}`,
                },
              });
              
              if (response.ok) {
                const summary = await response.json();
                
                // 연속 입력 일수에 따른 독려 메시지 생성
                let encouragementMessage = '';
                const current_streak = summary.current_streak || 0;
                if (current_streak > 0) {
                  if (current_streak >= 30) {
                    encouragementMessage = `🔥 ${current_streak}일 연속 입력 달성! 정말 대단합니다!`;
                  } else if (current_streak >= 14) {
                    encouragementMessage = `🔥 ${current_streak}일 연속 입력 달성! 훌륭합니다!`;
                  } else if (current_streak >= 7) {
                    encouragementMessage = `🔥 ${current_streak}일 연속 입력 달성! 잘하고 있어요!`;
                  } else {
                    encouragementMessage = `🔥 ${current_streak}일 연속 입력 달성!`;
                  }
                }
                
                // 브라우저 알림 표시
                let body = '';
                if (summary.has_data) {
                  if (encouragementMessage) {
                    body = `${encouragementMessage}\n오늘 ${summary.total_hours}시간 (${summary.count}건) 입력되었습니다.`;
                  } else {
                    body = `오늘 ${summary.total_hours}시간 (${summary.count}건) 입력되었습니다.`;
                  }
                } else {
                  if (encouragementMessage) {
                    body = `${encouragementMessage}\n오늘의 업무 투입시간을 입력해주세요.`;
                  } else {
                    body = '오늘의 업무 투입시간을 확인하고 입력해주세요.';
                  }
                }
                
                this.showNotification('OnTimeLog 투입시간 알림', {
                  body: body,
                  tag: 'daily-time-notification',
                  requireInteraction: false,
                  duration: 10000, // 10초
                  onClick: () => {
                    // 모바일/PC 경로 자동 감지
                    const isMobileDevice = window.innerWidth <= 768;
                    const path = isMobileDevice ? '/m/log' : '/p/job-time-input';
                    window.location.href = path;
                  },
                });

                // Service Worker에도 알림 요청 (백그라운드 알림용)
                await this.requestServiceWorkerNotification(summary);

                // 모달 표시
                this.showDailyTimeModal();
                return;
              } else if (response.status === 401) {
                // 401 에러면 인증 토큰이 만료된 것으로 간주
                console.warn('[NotificationService] 인증 토큰 만료 또는 무효');
                // 아래 로그인 유도 알림으로 진행
              }
            } catch (error) {
              console.error('[NotificationService] 당일 투입시간 조회 실패:', error);
              // 아래 로그인 유도 알림으로 진행
            }
          }

          // 인증 토큰이 없거나 API 호출 실패 시 로그인 유도 알림 표시
          const loginBody = 'OnTimeLog 시간을 입력하셨나요? 여기를 클릭하시면 바로 접속이 가능합니다.';
          
          this.showNotification('OnTimeLog 투입시간 알림', {
            body: loginBody,
            tag: 'daily-time-notification',
            requireInteraction: false,
            duration: 10000,
            onClick: () => {
              window.location.href = '/login';
            },
          });

          // Service Worker에도 로그인 유도 알림 요청
          await this.requestServiceWorkerNotification(null, false);

          // 마지막 알림 시간 기록
          lastNotificationTimes[timeKey] = now.getTime();
        }
      }
    };

    // 매 60초(1분)마다 확인 (운영 환경 부담 최소화)
    // 설정된 시간에 정확히 실행되도록 하기 위해 매 분 정각에 확인
    const intervalId = setInterval(checkAndNotify, 60000);
    
    // 즉시 확인 (저장 직후 즉시 알림을 위해)
    // DB 저장이 완료된 후 호출되므로 약간의 지연을 두어 안정적으로 작동
    setTimeout(() => {
      checkAndNotify();
    }, 500);
    
    // 개발 환경에서만 로그 출력
    if (process.env.NODE_ENV === 'development') {
      console.log('[NotificationService] 알림 스케줄러 시작됨 (매 60초마다 확인)');
    }

    // intervalId 저장 (나중에 정리할 수 있도록)
    this.dailyTimeNotificationIntervalId = intervalId;
  }

  // 당일 투입시간 알림 스케줄러 중지
  stopDailyTimeNotificationScheduler() {
    if (this.dailyTimeNotificationIntervalId) {
      clearInterval(this.dailyTimeNotificationIntervalId);
      this.dailyTimeNotificationIntervalId = null;
    }
  }

  // 테스트용: 스케줄러의 checkAndNotify 함수를 직접 호출
  async triggerDailyTimeNotificationTest() {
    console.log('[NotificationService] 테스트 모드: 당일 투입시간 알림 트리거');
    
    // 인증 토큰 확인
    const token = localStorage.getItem('token');
    const isAuthenticated = !!token;

      // 당일 투입시간 요약 조회 (인증된 경우만)
      if (isAuthenticated) {
        try {
          const today = new Date().toISOString().split('T')[0];
          const response = await fetch(`/api/v1/timelogs/daily-summary?target_date=${today}`, {
            credentials: 'include',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
          
          if (response.ok) {
            const summary = await response.json();
            
            // 연속 입력 일수에 따른 독려 메시지 생성
            let encouragementMessage = '';
            const current_streak = summary.current_streak || 0;
            if (current_streak > 0) {
              if (current_streak >= 30) {
                encouragementMessage = `🔥 ${current_streak}일 연속 입력 달성! Great Job!!`;
              } else if (current_streak >= 14) {
                encouragementMessage = `🔥 ${current_streak}일 연속 입력 달성! 훌륭합니다!`;
              } else if (current_streak >= 7) {
                encouragementMessage = `🔥 ${current_streak}일 연속 입력 달성! 잘하고 있어요!`;
              } else {
                encouragementMessage = `🔥 ${current_streak}일 연속 입력 달성!`;
              }
            }
            
            // 브라우저 알림 표시
            let body = '';
            if (summary.has_data) {
              if (encouragementMessage) {
                body = `${encouragementMessage}\n오늘 ${summary.total_hours}시간 (${summary.count}건) 입력되었습니다.`;
              } else {
                body = `오늘 ${summary.total_hours}시간 (${summary.count}건) 입력되었습니다.`;
              }
            } else {
              if (encouragementMessage) {
                body = `${encouragementMessage}\n오늘의 업무 투입시간을 입력해주세요.`;
              } else {
                body = '오늘의 업무 투입시간을 확인하고 입력해주세요.';
              }
            }
            
            this.showNotification('OnTimeLog 투입시간 알림', {
              body: body,
              tag: 'daily-time-notification',
              requireInteraction: false,
              duration: 10000,
              onClick: () => {
                const isMobileDevice = window.innerWidth <= 768;
                const path = isMobileDevice ? '/m/log' : '/p/job-time-input';
                window.location.href = path;
              },
            });

            // Service Worker에도 알림 요청
            await this.requestServiceWorkerNotification(summary);

            // 모달 표시
            this.showDailyTimeModal();
            return;
          } else if (response.status === 401) {
            console.warn('[NotificationService] 인증 토큰 만료 또는 무효');
          }
        } catch (error) {
          console.error('[NotificationService] 당일 투입시간 조회 실패:', error);
        }
      }

      // 인증 토큰이 없거나 API 호출 실패 시 로그인 유도 알림 표시
      const loginBody = 'OnTimeLog 시간을 입력하셨나요? 여기를 클릭하시면 바로 접속이 가능합니다.';
      
      this.showNotification('OnTimeLog 투입시간 알림', {
        body: loginBody,
        tag: 'daily-time-notification',
        requireInteraction: false,
        duration: 10000,
        onClick: () => {
          window.location.href = '/login';
        },
      });

      // Service Worker에도 로그인 유도 알림 요청
      await this.requestServiceWorkerNotification(null, false);
  }

  // ========== FCM 관련 메서드 ==========

  // FCM 토큰 요청 및 저장 (권한 상태 확인 포함)
  async requestFCMToken(userId) {
    if (!userId) {
      console.warn('[NotificationService] 사용자 ID가 없어 FCM 토큰을 요청할 수 없습니다.');
      return null;
    }

    // 1. Notification 권한 상태 확인
    const permission = Notification.permission;
    
    // 이미 승인된 경우: 토큰만 발급 (권한 요청 팝업 표시 안 함)
    if (permission === 'granted') {
      return await this.getAndSaveToken(userId);
    }
    
    // 거부된 경우: 브라우저 설정 변경 안내
    if (permission === 'denied') {
      console.warn('[NotificationService] 알림 권한이 거부되었습니다. 브라우저 설정에서 변경해주세요.');
      return null;
    }
    
    // default 상태: 권한 요청 (최초 1회만 팝업 표시)
    // 브라우저가 권한을 자동으로 저장하므로 이후에는 granted 상태로 유지됨
    const newPermission = await Notification.requestPermission();
    if (newPermission === 'granted') {
      return await this.getAndSaveToken(userId);
    }
    return null;
  }

  // FCM 토큰 발급 및 서버 저장
  async getAndSaveToken(userId) {
    if (!this.fcmMessaging) {
      // FCM이 초기화되지 않았으면 다시 시도
      await this.initializeFCM();
      if (!this.fcmMessaging) {
        // Firebase 설정이 없거나 FCM을 지원하지 않는 경우
        // 개발 환경에서만 로그 출력
        if (process.env.NODE_ENV === 'development') {
          console.log('[NotificationService] FCM이 초기화되지 않았습니다. (Firebase 설정이 없을 수 있음)');
        }
        return null;
      }
    }

    try {
      // 2. FCM 토큰 발급
      const token = await getFCMToken(this.fcmMessaging);
      
      if (token) {
        // 3. 서버에 토큰 저장 API 호출
        await this.saveTokenToServer(userId, token);
        this.fcmToken = token;
        return token;
      }
      return null;
    } catch (error) {
      console.error('[NotificationService] FCM 토큰 발급 실패:', error);
      return null;
    }
  }

  // 서버에 FCM 토큰 저장
  async saveTokenToServer(userId, token) {
    try {
      console.log('[NotificationService] FCM 토큰 저장 시작:', {
        userId: userId,
        tokenLength: token?.length || 0,
        url: apiConfig.FCM.SAVE_TOKEN
      });
      
      const response = await fetch(apiConfig.FCM.SAVE_TOKEN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          token: token,
          device_info: navigator.userAgent,
          platform: this.detectPlatform(),
        }),
      });

      if (response.ok) {
        const responseData = await response.json().catch(() => ({}));
        console.log('[NotificationService] FCM 토큰 저장 성공:', {
          status: response.status,
          response: responseData
        });
        return true;
      } else {
        const errorData = await response.json().catch(() => ({ detail: response.statusText }));
        const errorInfo = {
          status: response.status,
          statusText: response.statusText,
          message: errorData.detail || errorData.message || '알 수 없는 오류',
          url: apiConfig.FCM.SAVE_TOKEN
        };
        
        if (response.status === 500) {
          console.error('[NotificationService] FCM 토큰 저장 500 오류 - 서버 내부 오류:', errorInfo, errorData);
        } else if (response.status === 401) {
          console.warn('[NotificationService] FCM 토큰 저장 401 오류 - 인증 실패:', errorInfo);
        } else if (response.status === 404) {
          console.warn('[NotificationService] FCM 토큰 저장 404 오류 - API 엔드포인트 없음:', errorInfo);
        } else {
          console.error('[NotificationService] FCM 토큰 저장 실패:', errorInfo);
        }
        return false;
      }
    } catch (error) {
      const errorInfo = {
        message: error.message,
        name: error.name,
        code: error.code,
        url: apiConfig.FCM.SAVE_TOKEN
      };
      
      if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
        console.error('[NotificationService] FCM 토큰 저장 네트워크 오류 - 연결 실패:', errorInfo);
      } else {
        console.error('[NotificationService] FCM 토큰 저장 중 오류:', errorInfo, error);
      }
      return false;
    }
  }

  // 플랫폼 감지
  detectPlatform() {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('win')) return 'Windows';
    if (userAgent.includes('mac')) return 'macOS';
    if (userAgent.includes('linux')) return 'Linux';
    if (userAgent.includes('android')) return 'Android';
    if (userAgent.includes('iphone') || userAgent.includes('ipad')) return 'iOS';
    return 'Unknown';
  }

  // FCM 토큰 삭제 (로그아웃 시)
  async deleteTokenFromServer() {
    try {
      const response = await fetch(apiConfig.FCM.DELETE_TOKEN, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        this.fcmToken = null;
        console.log('[NotificationService] FCM 토큰이 서버에서 삭제되었습니다.');
        return true;
      } else {
        // 404 등은 정상 (토큰이 없을 수도 있음)
        if (response.status === 404) {
          this.fcmToken = null;
          return true;
        }
        console.error('[NotificationService] FCM 토큰 삭제 실패:', response.status);
        return false;
      }
    } catch (error) {
      console.error('[NotificationService] FCM 토큰 삭제 중 오류:', error);
      return false;
    }
  }

  // 현재 FCM 토큰 가져오기
  getCurrentToken() {
    return this.fcmToken;
  }

  // FCM 토큰 갱신 처리 (토큰이 자동으로 갱신될 때 호출)
  async handleTokenRefresh() {
    if (!this.fcmMessaging) {
      return null;
    }

    try {
      const newToken = await getFCMToken(this.fcmMessaging);
      if (newToken && newToken !== this.fcmToken) {
        // 토큰이 변경되었으면 서버에 업데이트
        const userId = this.getCurrentUserId();
        if (userId) {
          await this.saveTokenToServer(userId, newToken);
          this.fcmToken = newToken;
          console.log('[NotificationService] FCM 토큰이 갱신되었습니다.');
        }
        return newToken;
      }
      return this.fcmToken;
    } catch (error) {
      console.error('[NotificationService] FCM 토큰 갱신 실패:', error);
      return null;
    }
  }

  // 사용자 ID 가져오기 (JWT 토큰에서 추출)
  getCurrentUserId() {
    const token = localStorage.getItem('token');
    if (!token) {
      return null;
    }
    
    try {
      // JWT 토큰 디코딩 (Base64)
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }
      
      const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const decoded = JSON.parse(atob(payload));
      
      // employee_code 또는 sub 반환
      return decoded.employee_code || decoded.sub || localStorage.getItem('employeeCode') || null;
    } catch (error) {
      console.error('[NotificationService] 토큰 디코딩 실패:', error);
      return localStorage.getItem('employeeCode') || null;
    }
  }
}

// 싱글톤 인스턴스
const notificationService = new NotificationService();

export default notificationService;

